import React, { CSSProperties } from 'react';

const imageModules = import.meta.glob('../images/**/*.{jpg,jpeg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const imagesByFileName: Record<string, string> = {};

Object.entries(imageModules).forEach(([path, src]) => {
  const fileName = path.split('/').pop();
  if (fileName) {
    imagesByFileName[fileName] = src;
  }
});

export function getImageSrc(fileName: string): string | undefined {
  return imagesByFileName[fileName];
}

interface SiteImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  imgStyle?: CSSProperties;
}

/**
 * Drop-in stand-in for GatsbyImage/StaticImage that keeps the
 * `.gatsby-image-wrapper` class so existing layout CSS still applies.
 */
export const SiteImage: React.FC<SiteImageProps> = ({
  src,
  alt = '',
  className = '',
  style,
  objectFit = 'cover',
  objectPosition = '50% 50%',
  imgStyle,
}) => {
  return (
    <div
      className={`gatsby-image-wrapper ${className}`.trim()}
      style={{
        display: 'grid',
        width: '100%',
        height: '100%',
        ...style,
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          gridArea: '1/1',
          width: '100%',
          height: '100%',
          objectFit,
          objectPosition,
          margin: 0,
          padding: 0,
          ...imgStyle,
        }}
      />
    </div>
  );
};

interface FileImageProps extends Omit<SiteImageProps, 'src'> {
  fileName: string;
}

export const FileImage: React.FC<FileImageProps> = ({
  fileName,
  ...props
}) => {
  const src = getImageSrc(fileName);
  if (!src) return null;
  return <SiteImage src={src} {...props} />;
};
